import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { ASSETS } from "../assets";
import { CryptoHeroBackground } from "./CryptoHeroBackground";
import { trackCta } from "../lib/analytics";

export const HeroSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCta = (id) => {
    try { trackCta(id, { location: "hero" }); } catch {}
    scrollTo(id === "hero_explore_trendia" ? "trendia" : "topwallet");
  };

  return (
    <section
      className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-black pt-32 pb-48 md:pt-36 md:pb-56"
      data-testid="hero-section"
    >
      {/* Distant ambient image (very faded) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${ASSETS.hero_bg})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black" />

      {/* Cinematic crypto animation layer */}
      <CryptoHeroBackground />

      {/* Color glows */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,140,0,0.18),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(168,85,247,0.12),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_70%,rgba(0,229,255,0.10),transparent_50%)]" />

      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-20 mx-auto flex max-w-6xl flex-col items-center px-6 text-center md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 backdrop-blur-xl"
          data-testid="hero-eyebrow"
        >
          <Sparkles className="h-3.5 w-3.5 text-orange-400" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/80 md:text-xs">
            {t.hero.eyebrow}
          </span>
        </motion.div>

        <motion.img
          src={ASSETS.logos.rebel}
          alt="REBEL"
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="mb-10 w-[280px] drop-shadow-[0_0_60px_rgba(255,140,0,0.35)] md:w-[480px] lg:w-[560px]"
          data-testid="hero-rebel-logo"
        />

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="font-display max-w-4xl text-balance text-4xl font-black leading-[0.95] tracking-tighter text-white sm:text-5xl md:text-6xl lg:text-7xl"
          data-testid="hero-title"
        >
          {t.hero.title_1}{" "}
          <span className="bg-gradient-to-br from-orange-300 via-orange-500 to-amber-600 bg-clip-text text-transparent">
            {t.hero.title_2}
          </span>{" "}
          {t.hero.title_3}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-white/60 md:text-lg"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-12 flex flex-col items-center gap-4 sm:flex-row"
        >
          <button
            onClick={() => handleCta("hero_explore_trendia")}
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] md:text-base"
            data-testid="hero-cta-trendia"
          >
            <span className="relative z-10">{t.hero.cta_primary}</span>
            <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          </button>
          <button
            onClick={() => {
              try { trackCta("hero_explore_topwallet", { location: "hero" }); } catch { /* ignore */ }
              // External destination: open the standalone TopWallet site
              // in a new tab per user directive. `noopener,noreferrer` for
              // security (target=_blank without them exposes window.opener).
              window.open("https://topwallet.cloud/inicio", "_blank", "noopener,noreferrer");
            }}
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] md:text-base"
            data-testid="hero-cta-topwallet"
          >
            <span className="relative z-10">{t.hero.cta_secondary}</span>
            <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          </button>
          <div className="rebel-coin-btn-wrapper">
            <button
              onClick={() => {
                try { trackCta("hero_explore_rbl", { location: "hero" }); } catch {}
                navigate("/shadow/tokens");
              }}
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] md:text-base"
              data-testid="hero-cta-rbl-coin"
            >
              <span className="relative z-10">RE₿EL Coin 🔥</span>
              <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            </button>
            {[...Array(6)].map((_, i) => (
              <span
                key={i}
                className="rebel-spark"
                style={{
                  left: `${12 + i * 14}%`,
                  background: i % 2 === 0 ? "#f97316" : "#fbbf24",
                  animation: `rebel-spark ${0.7 + i * 0.1}s ease-out infinite ${i * 0.15}s`,
                  ["--dx"]: `${(i % 2 === 0 ? 1 : -1) * (4 + i * 3)}px`,
                }}
              />
            ))}
          </div>
          <button
            onClick={() => {
              try { trackCta("hero_explore_liqmap", { location: "hero" }); } catch { /* ignore */ }
              navigate("/liquidity-map");
            }}
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-bold text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] md:text-base"
            data-testid="hero-cta-liquidity-map"
          >
            <span className="relative z-10">Liquidity Map</span>
            <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
